// The merged WhatsApp GROUP view — the client half of a read-only projection that shows one
// row per WhatsApp group instead of one row per clinic gateway.
//
// Why the duplicates exist: each clinic branch runs its own WhatsApp gateway, and the backend
// keys threads by (group jid, gateway). Five clinic phones in one group means five thread
// documents holding near-identical histories, so the Groups list showed "TSEC Family @Orchard"
// five times, each with its own unread counter.
//
// NOT the same thing as merged-threads.js. That merges a PATIENT's threads across clinic
// numbers and INTERLEAVES their histories, because a patient's Bedok and Camden conversations
// are genuinely different. A group is the opposite: every member phone receives the same
// messages, so the merged row shows ONE canonical history and interleaving would render every
// message N times. The only thing the two share is the reply-from picker.
//
// This file is the ONE place the server contract is named — change the constants below and
// nothing else if the backend renames a field. No React, no I/O, so it stays unit-testable.

import { getThreadRowId } from "@/lib/messages/messages-helpers";
import {
  deriveClinicShortName,
  numberLabelForThread,
  threadWhatsappBotId,
} from "@/lib/messages/merged-threads";
import { isWaGroupThread, waGroupJid } from "@/lib/messages/wa-groups";

// ── SERVER CONTRACT (GET /api/threads?scope=groups&merge=groups) ────────────────────────────
/** Query param that asks the server for merged group rows. */
export const MERGE_WA_GROUPS_PARAM = "merge";
export const MERGE_WA_GROUPS_VALUE = "groups";
const F_ENVELOPE = "mergedGroup";
const F_GROUP_JID = "groupJid";
const F_THREAD_IDS = "threadIds";
const F_SOURCES = "sources";
const F_UNREAD_TOTAL = "unreadTotal";
// Per-source keys: threadId, whatsappBotId, whatsappNumberLabel, lastMessageAt, unreadCount.

const EMPTY_SOURCES = Object.freeze([]);

/** The `mergedGroup` envelope, or null when the row is not a merged one. */
function envelopeOf(thread) {
  const raw = thread?.[F_ENVELOPE];
  return raw && typeof raw === "object" ? raw : null;
}

/**
 * The stable identity of a merged row.
 *
 * Prefer the server's `groupJid`: the CANONICAL THREAD ID IS NOT STABLE. Gateways receive the
 * same message microseconds apart, so `lastMessageAt` jitters and whichever sibling is freshest
 * — and therefore canonical — can change between refreshes. Callers keying rows, selection or
 * scroll state must key on this, not on `threadId`.
 *
 * Falls back to `metadata.customThreadId` (via the `@g.us`-guarded `waGroupJid`) so the client
 * can still identify a group before the server grows the field.
 */
export function waGroupMergeKey(thread) {
  const jid = String(envelopeOf(thread)?.[F_GROUP_JID] || "").trim().toLowerCase();
  return jid || waGroupJid(thread);
}

/**
 * The clinic numbers this row spans, newest-activity first: one entry per sibling thread.
 *
 * Shape matches `buildMergedSources` in merged-threads.js ({threadId, botId, label, shortName,
 * lastActivityAt}) plus `unreadCount`, so `ReplyFromPicker` and `sourceSubtitle` render a group
 * source exactly like a patient source.
 *
 * Degrades to a single synthetic source built from the row itself when the envelope is absent
 * or malformed — which makes every `length > 1` check downstream correctly false, so an
 * unmerged row behaves exactly as it does today.
 */
export function getMergedGroupSources(thread, whatsappNumbers) {
  if (!thread) return EMPTY_SOURCES;
  const raw = envelopeOf(thread)?.[F_SOURCES];
  const sources = Array.isArray(raw) ? raw.filter((s) => s && typeof s === "object") : [];
  if (sources.length === 0) {
    const threadId = getThreadRowId(thread);
    if (!threadId) return EMPTY_SOURCES;
    const label = numberLabelForThread(thread, whatsappNumbers);
    return [
      {
        threadId,
        botId: threadWhatsappBotId(thread),
        label,
        shortName: deriveClinicShortName(label),
        lastActivityAt: thread?.lastMessageAt || null,
        unreadCount: Number(thread?.unreadCount) || 0,
      },
    ];
  }
  return sources
    .map((s) => {
      const threadId = String(s.threadId || "").trim();
      if (!threadId) return null;
      // Resolve the label through the same helper the unmerged badge uses, so a clinic reads
      // identically in the row, the picker and the patient merged view.
      const label = numberLabelForThread(
        { whatsappNumberLabel: s.whatsappNumberLabel, whatsappBotId: s.whatsappBotId },
        whatsappNumbers,
      );
      return {
        threadId,
        botId: String(s.whatsappBotId || "").trim(),
        label,
        shortName: deriveClinicShortName(label),
        lastActivityAt: s.lastMessageAt || null,
        unreadCount: Number(s.unreadCount) || 0,
      };
    })
    .filter(Boolean);
}

/** Whether this row actually stands for more than one clinic's copy of the same group. */
export function isMergedGroupRow(thread) {
  return isWaGroupThread(thread) && getMergedGroupSources(thread).length > 1;
}

/**
 * Every sibling thread id, CANONICAL FIRST — what mark-as-read fans out to.
 *
 * Unions the envelope's `threadIds`, its `sources[].threadId` and the row's own id, deduped,
 * so a partially-populated envelope still yields a usable set. Returns exactly `[rowId]` for an
 * unmerged row, which keeps every call site's non-merged path byte-identical.
 *
 * `unreadOnly` narrows to siblings that actually have unread — seven gateways would otherwise
 * mean seven POSTs on every row open.
 */
export function mergedGroupThreadIds(thread, { unreadOnly = false } = {}) {
  const rowId = getThreadRowId(thread);
  const envelope = envelopeOf(thread);
  const out = [];
  const seen = new Set();
  const push = (id) => {
    const tid = String(id || "").trim();
    if (!tid || seen.has(tid)) return;
    seen.add(tid);
    out.push(tid);
  };
  if (unreadOnly) {
    for (const s of getMergedGroupSources(thread)) {
      if (s.unreadCount > 0) push(s.threadId);
    }
    return out;
  }
  push(rowId);
  for (const id of Array.isArray(envelope?.[F_THREAD_IDS]) ? envelope[F_THREAD_IDS] : []) push(id);
  for (const s of getMergedGroupSources(thread)) push(s.threadId);
  return out;
}

/**
 * The merged row's unread count.
 *
 * The server sends `unreadTotal` as the MAX across siblings, never the sum: the same WhatsApp
 * message lands in every sibling thread, so summing would report N times the real backlog (ten
 * unread across five clinic phones would read as fifty). The local fallbacks below reduce the
 * same way for the same reason.
 */
export function mergedGroupUnreadTotal(thread) {
  const envelope = envelopeOf(thread);
  const total = Number(envelope?.[F_UNREAD_TOTAL]);
  if (Number.isFinite(total) && total > 0) return total;
  let best = 0;
  for (const s of getMergedGroupSources(thread)) {
    if (s.unreadCount > best) best = s.unreadCount;
  }
  if (best > 0) return best;
  return Number(thread?.unreadCount ?? thread?.unread_count) || 0;
}

/**
 * Sibling thread id → canonical row id, for translating realtime events.
 *
 * A WS message arriving on a NON-canonical sibling names a thread that is not in the list
 * window's index, so a row patch keyed on it would silently no-op and the merged row would go
 * stale until the next refresh. Unmerged rows contribute nothing, and no self-entries are
 * written, so a lookup miss safely means "use the id as-is".
 */
export function buildMergedGroupIndex(rows) {
  const index = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    if (!isMergedGroupRow(row)) continue;
    const canonical = getThreadRowId(row);
    if (!canonical) continue;
    for (const id of mergedGroupThreadIds(row)) {
      if (id !== canonical) index.set(id, canonical);
    }
  }
  return index;
}
